'use client';

import { useMemo, useContext, useState } from 'react';
import { verifyVerificationCode, send2FALink } from '@/_api/auth';
import { LoginContext } from '@/_contexts/LoginProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const useSignInWithCode = () => {
  const { uid, setUid, setLoginState, setTwoFactorMessage } =
    useContext(LoginContext);
  const { addToast } = useContext(ToastContext);
  const { isAdvisor, user, signInWithWorkOS } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [isLoading, setLoading] = useState<boolean>(false);

  const shouldShow2FA: boolean = useMemo(
    () =>
      Boolean(
        (user?.enabled_2fa ||
          (isAdvisor && preferences?.force_2fa_on_firm_users) ||
          (!isAdvisor && preferences?.force_2fa_on_users)) &&
          user?.phone_number,
      ),
    [
      user?.enabled_2fa,
      isAdvisor,
      user?.phone_number,
      preferences?.force_2fa_on_firm_users,
      preferences?.force_2fa_on_users,
    ],
  );

  const send2FAandSetState = async () => {
    setUid(user?.uid);

    await send2FALink({ uid: user?.uid! });
    (globalThis as any).IS_LOGIN_ATTEMPT = false;

    return setLoginState('2fa');
  };

  const signInWithVerificationCode = async (verificationCode: string) => {
    if (!uid) throw new Error('no UID provided for verification');

    try {
      setLoading(true);

      const response = (await verifyVerificationCode({
        uid,
        verificationCode,
      })) as unknown as Response; // TODO: `_callBackend` refactoring needed. See SOR-13473.

      const { status } = response;
      const responseData: any = await response.json();

      const {
        auth_token,
        success,
        user,
        requires_2fa: requiresTwoFactor = false,
        message: responseMessage,
      } = responseData;

      switch (status) {
        case 200:
          break;
        case 401:
          throw new Error('Invalid or expired verification code');
        case 429:
          throw new Error('Too many incorrect attempts');
        default:
          throw new Error('Something went wrong');
      }

      if (requiresTwoFactor) {
        if (responseMessage) {
          setTwoFactorMessage(responseMessage);
        }

        setLoginState('2fa');
        (globalThis as any).IS_LOGIN_ATTEMPT = false;

        try {
          await send2FALink({ uid });
        } catch (sendError: any) {
          addToast(
            'error',
            'Sorry, there was a problem sending a two-factor authentication code. Please try again later.',
            { error: sendError },
          );
        }

        return;
      }

      // WorkOS flow - use returned user data directly
      if (success && user && auth_token) {
        await signInWithWorkOS({
          auth_token,
          user,
        });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      addToast('error', error.message, { error });
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  return {
    isLoading,
    shouldShow2FA,
    send2FAandSetState,
    signInWithVerificationCode,
  };
};
